// src/prototype/components/oir/report/OIRSectionOpportunityLandscape.tsx

import React, { useMemo } from 'react';

/** Values from mapReportToOpportunities are already in $M — do not re-scale. */
const formatRoiValue = (v: number): string =>
  (Number.isFinite(v) ? v : 0).toFixed(1);

interface Opportunity {
  id: string;
  category: string;
  roiEstimate: { low: number; high: number; unit?: string };
}

interface OIRSectionOpportunityLandscapeProps {
  rankedOpportunities: Opportunity[];
}

function sumDomainRoi(opps: Opportunity[]): { low: number; high: number } {
  const low = opps.reduce((s, o) => s + (o.roiEstimate.low || 0), 0);
  const high = opps.reduce((s, o) => s + (o.roiEstimate.high || 0), 0);
  // Guard against any residual inverted pairs at the aggregate level
  return { low: Math.min(low, high), high: Math.max(low, high) };
}

export const OIRSectionOpportunityLandscape: React.FC<OIRSectionOpportunityLandscapeProps> = ({
  rankedOpportunities,
}) => {
  // Group by category
  const categoryMap = useMemo(() => {
    return rankedOpportunities.reduce((acc, opp) => {
      if (!acc[opp.category]) acc[opp.category] = [];
      acc[opp.category].push(opp);
      return acc;
    }, {} as Record<string, Opportunity[]>);
  }, [rankedOpportunities]);

  // Sort categories by count desc
  const sortedCategories = useMemo(() => {
    return Object.entries(categoryMap).sort((a, b) => b[1].length - a[1].length);
  }, [categoryMap]);

  const totalCategories = sortedCategories.length;
  const top2Categories = sortedCategories.slice(0, 2);
  const top2Pct = Math.round(
    (top2Categories.reduce((s, [, opps]) => s + opps.length, 0) / rankedOpportunities.length) * 100
  );
  const topCategory = sortedCategories[0];
  const topCategoryRoi = topCategory ? sumDomainRoi(topCategory[1]) : null;
  // Largest domain is featured in the accent banner — omit it from the grid to avoid duplication
  const remainingCategories = sortedCategories.slice(1);

  return (
    <div className="bg-white border border-[#e5e7eb] rounded-xl p-5 mb-5">
      {/* Section header */}
      <div className="mb-4">
        <h2 className="text-base font-bold text-[#18365E]">Opportunity Landscape</h2>
        <p className="text-[11px] text-[#717182] mt-0.5">
          Distribution of evaluated use cases across strategic domains
        </p>
      </div>

      {/* Narrative block */}
      <p className="text-sm text-[#18365E]/80 leading-relaxed mb-5">
        The opportunity landscape spans <strong>{rankedOpportunities.length}</strong> use cases
        across <strong>{totalCategories}</strong> strategic domains. The largest clusters are{' '}
        <strong>{top2Categories[0]?.[0]}</strong>
        {top2Categories[1] ? (
          <>
            {' '}
            and <strong>{top2Categories[1]?.[0]}</strong>
          </>
        ) : null}
        , which together represent <strong>{top2Pct}%</strong> of the evaluated portfolio.
      </p>

      {/* Top category accent card */}
      {topCategory && topCategoryRoi && (
        <div className="mb-4 p-3.5 rounded-xl bg-[#18365E] text-white flex items-center justify-between">
          <div>
            <p className="text-[9px] font-semibold uppercase tracking-widest text-white/60 mb-0.5">
              Largest Domain
            </p>
            <p className="text-sm font-bold">{topCategory[0]}</p>
            <p className="text-[11px] text-white/70 mt-0.5">
              {topCategory[1].length} use case{topCategory[1].length !== 1 ? 's' : ''}
            </p>
          </div>
          <div className="text-right">
            <p className="text-[9px] text-white/60 uppercase tracking-wide mb-0.5">ROI Range</p>
            <p className="text-sm font-bold text-[#FF6E4C]">
              ${formatRoiValue(topCategoryRoi.low)}–${formatRoiValue(topCategoryRoi.high)}M
            </p>
          </div>
        </div>
      )}

      {/* Remaining domains only (largest already shown above) */}
      {remainingCategories.length > 0 && (
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
          {remainingCategories.map(([cat, opps]) => {
            const { low: catRoiLow, high: catRoiHigh } = sumDomainRoi(opps);

            return (
              <div
                key={cat}
                className="rounded-xl border border-[#e5e7eb] bg-[#F5F7FA] p-3"
              >
                <div className="flex items-start justify-between gap-1 mb-1.5">
                  <p className="text-[11px] font-semibold text-[#18365E] leading-tight">{cat}</p>
                  <span className="text-[10px] font-bold text-[#717182] shrink-0 bg-white border border-[#e5e7eb] rounded px-1.5 py-0.5">
                    {opps.length}
                  </span>
                </div>
                <p className="text-[10px] text-[#717182]">
                  ${formatRoiValue(catRoiLow)}–${formatRoiValue(catRoiHigh)}M
                </p>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};